'use client'

import React, { useState, useEffect, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { 
  Bot, 
  Play, 
  Pause, 
  Calendar, 
  CheckCircle, 
  AlertCircle, 
  Clock, 
  Target, 
  TrendingUp, 
  MessageSquare, 
  MessageCircle,
  ThumbsUp, 
  Zap,
  Brain,
  Eye,
  RefreshCw,
  ChevronRight,
  Sparkles,
  Activity,
  BarChart3,
  Users,
  Hash,
  Globe,
  Shield,
  Rocket,
  Terminal,
  Cpu,
  Database,
  Network,
  Code,
  Layers,
  GitBranch,
  Workflow,
  Monitor,
  Server,
  ArrowRight,
  Dot,
  ChevronDown,
  Filter,
  Search,
  MoreHorizontal,
  ExternalLink,
  X
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Progress } from '@/components/ui/progress'
import RedditAgentSetup from '@/components/reddit/RedditAgentSetup'
import RedditConnectionRequired from './RedditConnectionRequired'
import RedditCredentialStatus from './RedditCredentialStatus'

// Constants
const AGENT_ACTIVITY_WINDOW_HOURS = 24;
const NEXT_RUN_MINUTES = 15;
const METRICS_REFETCH_INTERVAL = 30000; // 30 seconds
const LOGS_REFETCH_INTERVAL = 30000; // 30 seconds
const PERFORMANCE_REFETCH_INTERVAL = 60000; // 1 minute
const MAX_LOGS_FETCH = 50;
const MAX_METRICS_LOGS = 100;
const TOP_SUBREDDITS_LIMIT = 5;
const TOP_DECISION_PATTERNS_LIMIT = 5;

interface AgentLogEntry {
  id: string
  timestamp: string
  action: 'content_generated_and_queued' | 'skipped_no_subreddit' | 'error_generating_content' | 'skipped_no_keywords' | 'agent_auto_paused' | 'validation_check' | 'subreddit_analysis' | 'content_optimization' | 'skipped_recent_content_exists' | 'skipped_no_posting_schedule' | 'skipped_duplicate_check_failed' | 'skipped_schedule_check_failed' | 'content_generation_started' | 'subreddit_selected' | 'topics_analyzed' | 'topic_generation_started' | 'topics_generated' | 'topic_selected' | 'content_creation_started' | 'content_scheduled'
  keywordUsed?: string
  subredditSelected?: string
  reasonForSelection: string
  statusMessage: string
  contentPreview?: string
  scheduledPostId?: string
  executionTime?: number
  confidence?: number
  metadata?: {
    apiCalls?: number
    tokensUsed?: number
    modelVersion?: string
    validationScore?: number
  }
}

interface AgentMetrics {
  postsGenerated: number
  postsPublished: number
  successRate: number
  nextRun: string
  isActive: boolean
  totalApiCalls: number
  postsAwaitingApproval: number
  isSetupComplete: boolean
}

function RedditAgentDashboardContent() {
  const { id: projectId } = useParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  const [selectedTab, setSelectedTab] = useState('agent-log')
  const [logFilter, setLogFilter] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [isUpdatingAgent, setIsUpdatingAgent] = useState(false)

  // Fetch Reddit agent metrics
  const { data: metrics, isLoading: isLoadingMetrics, error: metricsError } = useQuery({
    queryKey: ['reddit-agent-metrics', projectId],
    queryFn: async (): Promise<AgentMetrics> => {
      if (!projectId || !user?.id) {
        return {
          postsGenerated: 0,
          postsPublished: 0,
          successRate: 0,
          nextRun: new Date(Date.now() + NEXT_RUN_MINUTES * 60 * 1000).toISOString(),
          isActive: false,
          totalApiCalls: 0,
          postsAwaitingApproval: 0,
          isSetupComplete: false
        };
      }

      // Get project settings
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .select('enable_reddit_agent, reddit_agent_paused')
        .eq('id', projectId)
        .eq('user_id', user.id)
        .single();

      // Check setup completion status
      // 1. Check Reddit connection
      const { data: redditConnection, error: redditError } = await supabase
        .from('reddit_accounts')
        .select('*')
        .eq('project_id', projectId)
        .eq('user_id', user.id)
        .limit(1);

      const isRedditConnected = !redditError && redditConnection && redditConnection.length > 0;

      // 2. Check approved subreddits
      const { data: approvedSubreddits, error: subredditError } = await supabase
        .from('approved_subreddits')
        .select('*')
        .eq('project_id', projectId)
        .eq('user_id', user.id);

      const hasApprovedSubreddits = !subredditError && approvedSubreddits && approvedSubreddits.length > 0;

      // 3. Check posting schedule
      const { data: postingSlots, error: slotsError } = await supabase
        .from('reddit_posting_slots')
        .select('*')
        .eq('project_id', projectId)
        .eq('is_active', true);

      const hasActiveSlots = !slotsError && postingSlots && postingSlots.length > 0;

      // Setup is complete if all three requirements are met
      const isSetupComplete = isRedditConnected && hasApprovedSubreddits && hasActiveSlots;

      // Get scheduled posts count (ONLY agent-created posts)
      const { data: scheduledPosts, error: scheduledError } = await supabase
        .from('scheduled_reddit_posts' as any)
        .select('id, status, created_at')
        .eq('project_id', projectId)
        .eq('user_id', user.id)
        .in('status', ['queued_by_agent', 'approved_for_publishing', 'publishing', 'sent', 'error_posting']) // Only agent workflow statuses
        .or('status.eq.queued_by_agent,status.eq.approved_for_publishing,status.eq.publishing,status.eq.sent,status.eq.error_posting');

      // Get agent logs for metrics calculation and to cross-reference agent activity
      const { data: agentLogs, error: logsError } = await supabase
        .from('reddit_agent_log' as any)
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false })
        .limit(MAX_METRICS_LOGS);

      if (scheduledError || logsError) {
        console.error('Error fetching metrics:', { scheduledError, logsError });
      }

      // Only count posts that have agent activity OR were queued by agent
      const agentPostIds = new Set(agentLogs?.map((log: any) => log.post_id).filter(Boolean) || []);
      const agentScheduledPosts = scheduledPosts?.filter((post: any) => 
        post.status === 'queued_by_agent' || agentPostIds.has(post.id)
      ) || [];

      const totalPosts = agentScheduledPosts.length;
      const publishedPosts = agentScheduledPosts.filter((p: any) => p.status === 'posted' || p.status === 'sent').length;
      const pendingPosts = agentScheduledPosts.filter((p: any) => 
        p.status === 'pending' || p.status === 'awaiting_approval' || p.status === 'queued_by_agent' || p.status === 'approved_for_publishing'
      ).length;
      const successRate = totalPosts > 0 ? Math.round((publishedPosts / totalPosts) * 100) : 0;
      const totalApiCalls = agentLogs?.reduce((sum: number, log: any) => sum + (log.metadata?.apiCalls || 0), 0) || 0;

      // Agent is active if: setup is complete AND agent is enabled AND not paused
      const isActive = isSetupComplete && 
                      (project?.enable_reddit_agent === true) && 
                      (project?.reddit_agent_paused !== true);

      return {
        postsGenerated: totalPosts,
        postsPublished: publishedPosts,
        successRate,
        nextRun: new Date(Date.now() + NEXT_RUN_MINUTES * 60 * 1000).toISOString(),
        isActive: isActive || false,
        totalApiCalls,
        postsAwaitingApproval: pendingPosts,
        isSetupComplete
      };
    },
    enabled: !!projectId && !!user?.id,
    refetchInterval: METRICS_REFETCH_INTERVAL,
    retry: 2
  })

  // Fetch agent logs
  const { data: agentLog = [], isLoading: isLoadingLogs, error: logsError } = useQuery({
    queryKey: ['reddit-agent-logs', projectId],
    queryFn: async () => {
      if (!projectId || !user?.id) return [];

      const { data, error } = await supabase
        .from('reddit_agent_log')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false })
        .limit(MAX_LOGS_FETCH);

      if (error) {
        console.error('Error fetching agent logs:', error);
        return [];
      }

      // Transform to match the expected format
      return data?.map((log: any) => ({
        id: log.id,
        timestamp: log.created_at,
        action: log.action_type,
        keywordUsed: log.metadata?.keyword_used,
        subredditSelected: log.subreddit_name,
        reasonForSelection: log.decision_reasoning || 'Agent decision',
        statusMessage: log.success ? 'Operation completed successfully' : (log.error_message || 'Operation failed'),
        contentPreview: log.metadata?.content_preview,
        scheduledPostId: log.post_id,
        executionTime: log.metadata?.execution_time,
        confidence: log.metadata?.confidence,
        metadata: log.metadata
      })) || [];
    },
    enabled: !!projectId && !!user?.id,
    refetchInterval: LOGS_REFETCH_INTERVAL,
    retry: 2
  })

  // Fetch performance insights data from real backend
  const { data: performanceData = null, isLoading: isLoadingPerformance } = useQuery({
    queryKey: ['reddit-performance-insights', projectId],
    queryFn: async () => {
      if (!projectId || !user?.id) return null;

      // Get scheduled posts for subreddit performance analysis
      const { data: scheduledPosts, error: scheduledError } = await supabase
        .from('scheduled_reddit_posts')
        .select('*')
        .eq('project_id', projectId)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      // Get agent logs for keyword analysis
      const { data: agentLogs, error: logsError } = await supabase
        .from('reddit_agent_log')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (scheduledError || logsError) {
        console.error('Error fetching performance data:', { scheduledError, logsError });
        return null;
      }

      // Calculate top performing subreddits
      const subredditStats: { [key: string]: any } = {};
      scheduledPosts?.forEach((post: any) => {
        const subreddit = post.subreddit_name;
        if (!subreddit) return; // Skip posts without subreddit name
        
        if (!subredditStats[subreddit]) {
          subredditStats[subreddit] = {
            name: subreddit,
            posts: 0,
            published: 0,
            failed: 0
          };
        }
        subredditStats[subreddit].posts++;
        if (post.status === 'sent' || post.status === 'posted') {
          subredditStats[subreddit].published++;
        } else if (post.status === 'error_posting' || post.status === 'failed') {
          subredditStats[subreddit].failed++;
        }
      });

      const topSubreddits = Object.values(subredditStats)
        .map((sub: any) => ({
          ...sub,
          successRate: sub.posts > 0 ? Math.round((sub.published / sub.posts) * 100) : 0
        }))
        .sort((a: any, b: any) => (b.posts || 0) - (a.posts || 0))
        .slice(0, TOP_SUBREDDITS_LIMIT);

      // Calculate agent decision patterns
      const decisionStats: { [key: string]: any } = {};
      agentLogs?.forEach((log: any) => {
        const reason = log.reason_for_selection || log.decision_reasoning || 'Unknown reason';
        if (!decisionStats[reason]) {
          decisionStats[reason] = {
            reason,
            count: 0,
            successful: 0
          };
        }
        decisionStats[reason].count++;
        if (log.success && log.action_type === 'content_generated_and_queued') {
          decisionStats[reason].successful++;
        }
      });

      const topDecisionPatterns = Object.values(decisionStats)
        .map((pattern: any) => ({
          ...pattern,
          successRate: pattern.count > 0 ? Math.round((pattern.successful / pattern.count) * 100) : 0
        }))
        .sort((a: any, b: any) => (b.count || 0) - (a.count || 0))
        .slice(0, TOP_DECISION_PATTERNS_LIMIT);

      return {
        topSubreddits,
        topDecisionPatterns,
        totalPosts: scheduledPosts?.length || 0,
        totalLogs: agentLogs?.length || 0
      };
    },
    enabled: !!projectId && !!user?.id,
    refetchInterval: PERFORMANCE_REFETCH_INTERVAL,
    retry: 1
  })

  const getActionIcon = (action: AgentLogEntry['action']) => {
    switch (action) {
      case 'content_generated_and_queued': return <Cpu className="h-4 w-4 text-blue-500" />
      case 'validation_check': return <Shield className="h-4 w-4 text-green-500" />
      case 'subreddit_analysis': return <Database className="h-4 w-4 text-blue-500" />
      case 'content_optimization': return <Zap className="h-4 w-4 text-amber-500" />
      case 'skipped_no_subreddit': return <AlertCircle className="h-4 w-4 text-orange-500" />
      case 'error_generating_content': return <AlertCircle className="h-4 w-4 text-red-500" />
      case 'skipped_recent_content_exists': return <Clock className="h-4 w-4 text-blue-500" />
      case 'skipped_no_posting_schedule': return <Calendar className="h-4 w-4 text-yellow-500" />
      case 'skipped_duplicate_check_failed': return <AlertCircle className="h-4 w-4 text-red-600" />
      case 'skipped_schedule_check_failed': return <AlertCircle className="h-4 w-4 text-red-600" />
      case 'content_generation_started': return <Rocket className="h-4 w-4 text-blue-500" />
      case 'subreddit_selected': return <Target className="h-4 w-4 text-green-500" />
      case 'topics_analyzed': return <Eye className="h-4 w-4 text-blue-500" />
      case 'topic_generation_started': return <Brain className="h-4 w-4 text-blue-500" />
      case 'topics_generated': return <Sparkles className="h-4 w-4 text-blue-500" />
      case 'topic_selected': return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'content_creation_started': return <Zap className="h-4 w-4 text-orange-500" />
      case 'content_scheduled': return <Calendar className="h-4 w-4 text-blue-600" />
      default: return <Activity className="h-4 w-4 text-zinc-500" />
    }
  }

  const getActionColor = (action: AgentLogEntry['action']) => {
    switch (action) {
      case 'content_generated_and_queued': return 'bg-gradient-to-r from-blue-50 to-blue-50 border-blue-200'
      case 'validation_check': return 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
      case 'subreddit_analysis': return 'bg-gradient-to-r from-blue-50 to-violet-50 border-blue-200'
      case 'content_optimization': return 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200'
      case 'skipped_no_subreddit': return 'bg-gradient-to-r from-orange-50 to-red-50 border-orange-200'
      case 'error_generating_content': return 'bg-gradient-to-r from-red-50 to-pink-50 border-red-200'
      case 'skipped_recent_content_exists': return 'bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200'
      case 'skipped_no_posting_schedule': return 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200'
      case 'skipped_duplicate_check_failed': return 'bg-gradient-to-r from-red-100 to-pink-100 border-red-300'
      case 'skipped_schedule_check_failed': return 'bg-gradient-to-r from-red-100 to-pink-100 border-red-300'
      case 'content_generation_started': return 'bg-gradient-to-r from-blue-50 to-blue-50 border-blue-200'
      case 'subreddit_selected': return 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
      case 'topics_analyzed': return 'bg-gradient-to-r from-blue-50 to-sky-50 border-blue-200'
      case 'topic_generation_started': return 'bg-gradient-to-r from-blue-50 to-blue-50 border-blue-200'
      case 'topics_generated': return 'bg-gradient-to-r from-blue-50 to-pink-50 border-blue-200'
      case 'topic_selected': return 'bg-gradient-to-r from-green-50 to-teal-50 border-green-200'
      case 'content_creation_started': return 'bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200'
      case 'content_scheduled': return 'bg-gradient-to-r from-blue-50 to-blue-50 border-blue-300'
      default: return 'bg-gradient-to-r from-zinc-50 to-gray-50 border-zinc-200'
    }
  }

  const formatActionText = (action: AgentLogEntry['action']) => {
    switch (action) {
      case 'content_generated_and_queued': return 'Content Created'
      case 'validation_check': return 'Validation Check'
      case 'subreddit_analysis': return 'Subreddit Analysis'
      case 'content_optimization': return 'Content Optimization'
      case 'skipped_no_subreddit': return 'No Valid Subreddit'
      case 'error_generating_content': return 'Generation Error'
      case 'skipped_recent_content_exists': return 'Duplicate Prevention'
      case 'skipped_no_posting_schedule': return 'No Active Schedule'
      case 'skipped_duplicate_check_failed': return 'Safety Check Failed'
      case 'skipped_schedule_check_failed': return 'Schedule Check Failed'
      case 'content_generation_started': return 'Process Started'
      case 'subreddit_selected': return 'Subreddit Selected'
      case 'topics_analyzed': return 'Topics Analyzed'
      case 'topic_generation_started': return 'Generating Topics'
      case 'topics_generated': return 'Topics Generated'
      case 'topic_selected': return 'Topic Selected'
      case 'content_creation_started': return 'Creating Content'
      case 'content_scheduled': return 'Content Scheduled'
      default: return 'System Event'
    }
  }

  const toggleAgent = async () => {
    if (!projectId || !user?.id) return;
    
    // Check if trying to start agent without setup
    if (!metrics?.isActive && !metrics?.isSetupComplete) {
      toast({
        title: "Setup Required",
        description: "Please complete the agent setup before activating. Click 'Setup' to get started.",
        variant: "destructive"
      });
      return;
    }
    
    setIsUpdatingAgent(true);
    try {
      // Update the project settings to toggle the agent
      const { error } = await supabase
        .from('projects')
        .update({ 
          reddit_agent_paused: metrics?.isActive ? true : false 
        })
        .eq('id', projectId)
        .eq('user_id', user.id);

      if (error) {
        throw error;
      }

      toast({
        title: metrics?.isActive ? "Agent Paused" : "Agent Activated",
        description: metrics?.isActive 
          ? "Reddit Agent has been paused and will stop generating content."
          : "Reddit Agent is now active and will resume generating content.",
      });
      
      // Refetch data
      queryClient.invalidateQueries({ queryKey: ['reddit-agent-metrics', projectId] });
    } catch (error) {
      console.error('Error toggling agent:', error);
      toast({
        title: "Error",
        description: "Failed to update agent status. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsUpdatingAgent(false);
    }
  }

  const filteredLogs = agentLog.filter(log => {
    const matchesFilter = logFilter === 'all' || log.action === logFilter
    const matchesSearch = searchQuery === '' || 
      log.statusMessage.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.keywordUsed?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.subredditSelected?.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesFilter && matchesSearch
  })

  // Create safe metrics with defaults to prevent undefined access
  const safeMetrics = metrics || {
    postsGenerated: 0,
    postsPublished: 0,
    successRate: 0,
    nextRun: new Date(Date.now() + NEXT_RUN_MINUTES * 60 * 1000).toISOString(),
    isActive: false,
    totalApiCalls: 0,
    postsAwaitingApproval: 0,
    isSetupComplete: false
  };

  return (
    <div className="container py-6 max-w-7xl">
      <div className="flex flex-col space-y-8">
        {/* Header */}
        <motion.div 
          className="flex items-center justify-between"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight gradient-text">
                Reddit AI Agent
              </h1>
              <p className="text-zinc-500 mt-1">
                Autonomous content generation and distribution system
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              className="gap-2 border-zinc-200 hover:bg-zinc-50"
              onClick={() => {
                // Navigate to setup page using correct route
                navigate(`/projects/${projectId}/reddit/agent-setup`);
              }}
            >
              <Rocket className="h-4 w-4" />
              Setup
            </Button>
            
            <Button 
              onClick={toggleAgent}
              disabled={isUpdatingAgent || !safeMetrics.isSetupComplete}
              className={`gap-2 ${
                safeMetrics.isActive 
                  ? 'bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700' 
                  : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'
              } ${!safeMetrics.isSetupComplete ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isUpdatingAgent ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : safeMetrics.isActive ? (
                <>
                  <Pause className="h-4 w-4" />
                  Pause Agent
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  Start Agent
                </>
              )}
            </Button>
          </div>
        </motion.div>

        {/* Setup Banner - Show prominently when setup is not complete */}
        {!safeMetrics.isSetupComplete && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card className="border-0 shadow-lg bg-gradient-to-r from-blue-50 via-blue-50 to-blue-50 dark:from-blue-950/30 dark:via-blue-950/30 dark:to-blue-950/30 border border-blue-200 dark:border-blue-800/40">
              <CardContent className="p-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6">
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-blue-500 rounded-2xl blur-lg opacity-30"></div>
                      <div className="relative bg-gradient-to-r from-blue-500 to-blue-500 p-4 rounded-2xl">
                        <Rocket className="h-8 w-8 text-white" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 mb-2">
                        Setup Required to Activate AI Agent
                      </h3>
                      <p className="text-zinc-700 dark:text-zinc-300 text-lg leading-relaxed mb-3">
                        Complete the setup process to enable autonomous Reddit posting. The AI agent needs configuration before it can start generating and scheduling content.
                      </p>
                      <div className="flex items-center gap-6 text-sm text-zinc-600 dark:text-zinc-400">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                          <span>Reddit Connected</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          <span>Agent Configuration Pending</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-3">
                    <Button 
                      size="lg"
                      className="gap-3 bg-gradient-to-r from-blue-600 to-blue-600 hover:from-blue-700 hover:to-blue-700 text-white px-8 py-4 text-lg font-semibold"
                      onClick={() => {
                        navigate(`/projects/${projectId}/reddit/agent-setup`);
                      }}
                    >
                      <Rocket className="h-5 w-5" />
                      Complete Setup Now
                    </Button>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400 text-center">Required before activation</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Reddit Credential Status - Show for authenticated users */}
        {safeMetrics.isSetupComplete && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <RedditCredentialStatus 
              className="mb-6"
            />
          </motion.div>
        )}

        {/* System Status & Key Metrics */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-4 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {/* Agent Status */}
          <Card className="relative overflow-hidden border-0 shadow-lg bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700">
            <div className={`absolute inset-0 ${safeMetrics.isActive ? 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30' : 'bg-gradient-to-br from-zinc-50 to-gray-50 dark:from-zinc-900/50 dark:to-zinc-800/50'}`}></div>
            <CardContent className="relative p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">System Status</p>
                  <p className={`text-lg font-bold ${safeMetrics.isActive ? 'text-green-700 dark:text-green-400' : 'text-zinc-700 dark:text-zinc-300'}`}>
                    {safeMetrics.isActive ? 'Active' : 'Offline'}
                  </p>
                  {safeMetrics.isActive ? (
                    <div className="flex items-center gap-2 mt-1">
                      <motion.div
                        className="flex items-center gap-1"
                        initial={{ opacity: 0.3 }}
                        animate={{ opacity: [0.3, 1, 0.3] }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                      >
                        <motion.div 
                          className="w-2 h-2 bg-green-500 dark:bg-green-400 rounded-full"
                          animate={{ scale: [1, 1.3, 1] }}
                          transition={{ duration: 1.5, repeat: Infinity, delay: 0 }}
                        />
                        <motion.div 
                          className="w-2 h-2 bg-green-500 dark:bg-green-400 rounded-full"
                          animate={{ scale: [1, 1.3, 1] }}
                          transition={{ duration: 1.5, repeat: Infinity, delay: 0.2 }}
                        />
                        <motion.div 
                          className="w-2 h-2 bg-green-500 dark:bg-green-400 rounded-full"
                          animate={{ scale: [1, 1.3, 1] }}
                          transition={{ duration: 1.5, repeat: Infinity, delay: 0.4 }}
                        />
                      </motion.div>
                      <span className="text-xs text-green-600 dark:text-green-400 font-medium">Reddit Agent is live</span>
                    </div>
                  ) : (
                    <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">Agent is offline</p>
                  )}
                </div>
                <div className="flex flex-col items-end">
                  <div className={`w-3 h-3 rounded-full ${safeMetrics.isActive ? 'bg-green-500 dark:bg-green-400' : 'bg-zinc-400 dark:bg-zinc-500'} animate-pulse`}></div>
                  <span className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">Live</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Posts Generated */}
          <Card className="relative overflow-hidden border-0 shadow-lg bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-blue-50 dark:from-blue-950/30 dark:to-blue-950/30"></div>
            <CardContent className="relative p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">Content Generated</p>
                  <p className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">{safeMetrics.postsGenerated}</p>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                    Total posts created
                  </p>
                </div>
                <Brain className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
            </CardContent>
          </Card>

          {/* Success Rate */}
          <Card className="relative overflow-hidden border-0 shadow-lg bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/30 dark:to-green-950/30"></div>
            <CardContent className="relative p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">Success Rate</p>
                  <p className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">{safeMetrics.successRate}%</p>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                    {safeMetrics.postsPublished} published
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
              </div>
            </CardContent>
          </Card>

          {/* API Usage */}
          <Card className="relative overflow-hidden border-0 shadow-lg bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-violet-50 dark:from-blue-950/30 dark:to-violet-950/30"></div>
            <CardContent className="relative p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">API Calls</p>
                  <p className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">{safeMetrics.totalApiCalls.toLocaleString()}</p>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                    Next run: {new Date(safeMetrics.nextRun).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                <Network className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Main Content - Agent Intelligence Log */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Tabs value={selectedTab} onValueChange={setSelectedTab} className="space-y-6">
            <TabsList className="grid w-fit grid-cols-2 bg-zinc-100 dark:bg-zinc-800 p-1 rounded-xl">
              <TabsTrigger 
                value="agent-log" 
                className="data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-700 data-[state=active]:shadow-sm dark:data-[state=active]:text-zinc-100 text-zinc-600 dark:text-zinc-300 rounded-lg"
              >
                <Terminal className="h-4 w-4 mr-2" />
                Agent Intelligence Log
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-700 data-[state=active]:shadow-sm dark:data-[state=active]:text-zinc-100 text-zinc-600 dark:text-zinc-300 rounded-lg"
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                Performance Insights
              </TabsTrigger>
            </TabsList>

            <TabsContent value="agent-log" className="space-y-4">
              <Card className="border-0 shadow-lg bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700">
                <CardHeader className="bg-gradient-to-r from-zinc-50 to-white dark:from-zinc-900 dark:to-zinc-800 border-b border-zinc-200 dark:border-zinc-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-xl font-semibold text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
                        <Terminal className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        Agent Intelligence Log
                      </CardTitle>
                      <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
                        Real-time execution pipeline and decision-making process
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <Search className="h-4 w-4 text-zinc-400 dark:text-zinc-500" />
                        <Input
                          placeholder="Search logs..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-48 h-8 text-sm bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-900 dark:text-zinc-100"
                        />
                      </div>
                      <Select value={logFilter} onValueChange={setLogFilter}>
                        <SelectTrigger className="w-40 h-8 bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-900 dark:text-zinc-100">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700">
                          <SelectItem value="all" className="text-zinc-900 dark:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-700">All Events</SelectItem>
                          <SelectItem value="content_generated_and_queued" className="text-zinc-900 dark:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-700">Content Generated</SelectItem>
                          <SelectItem value="validation_check" className="text-zinc-900 dark:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-700">Validation</SelectItem>
                          <SelectItem value="subreddit_analysis" className="text-zinc-900 dark:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-700">Analysis</SelectItem>
                          <SelectItem value="skipped_recent_content_exists" className="text-zinc-900 dark:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-700">Duplicate Prevention</SelectItem>
                          <SelectItem value="skipped_no_posting_schedule" className="text-zinc-900 dark:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-700">Schedule Issues</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button size="sm" variant="outline" className="gap-2 h-8 border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800">
                        <RefreshCw className="h-3 w-3" />
                        Refresh
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="space-y-0">
                    {filteredLogs.map((entry, index) => (
                      <motion.div
                        key={entry.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        className={`border-b border-zinc-100 dark:border-zinc-700 p-6 transition-all duration-200 hover:bg-zinc-50/50 dark:hover:bg-zinc-700/30 ${getActionColor(entry.action)} dark:from-zinc-800/50 dark:via-zinc-800/30 dark:to-zinc-800/50`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="flex items-center gap-2">
                                {getActionIcon(entry.action)}
                                <Badge variant="outline" className="text-xs font-medium bg-white/80 dark:bg-zinc-800/80 border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300">
                                  {formatActionText(entry.action)}
                                </Badge>
                              </div>
                              
                              <div className="flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
                                <Clock className="h-3 w-3" />
                                {new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                              </div>
                              
                              {entry.executionTime && (
                                <Badge variant="secondary" className="text-xs bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400">
                                  <Zap className="h-3 w-3 mr-1" />
                                  {entry.executionTime}s
                                </Badge>
                              )}
                            </div>
                            
                            <p className="text-zinc-700 dark:text-zinc-300 mb-3 font-medium leading-relaxed">
                              {entry.statusMessage}
                            </p>
                            
                            {entry.contentPreview && (
                              <div className="bg-white/80 dark:bg-zinc-800/80 rounded-lg p-4 border border-zinc-200/50 dark:border-zinc-700/50 mb-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <Code className="h-4 w-4 text-zinc-500 dark:text-zinc-400" />
                                  <span className="text-sm font-medium text-zinc-600 dark:text-zinc-400">Generated Content:</span>
                                </div>
                                <p className="text-sm text-zinc-800 dark:text-zinc-200 italic leading-relaxed">"{entry.contentPreview}"</p>
                              </div>
                            )}
                            
                            <div className="flex items-center gap-6 text-xs text-zinc-500 dark:text-zinc-400">
                              {entry.subredditSelected && (
                                <div className="flex items-center gap-1">
                                  <Globe className="h-3 w-3" />
                                  <span>Target: r/{entry.subredditSelected}</span>
                                </div>
                              )}
                              {entry.metadata?.apiCalls && (
                                <div className="flex items-center gap-1">
                                  <Network className="h-3 w-3" />
                                  <span>API Calls: {entry.metadata.apiCalls}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2 ml-4">
                            {entry.scheduledPostId && (
                              <Button size="sm" variant="ghost" className="text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 h-8 w-8 p-0">
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" className="text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  
                  {filteredLogs.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-16 px-4">
                      <Terminal className="h-12 w-12 text-zinc-300 dark:text-zinc-600 mb-4" />
                      <h3 className="text-lg font-semibold text-zinc-600 dark:text-zinc-400 mb-2">No logs found</h3>
                      <p className="text-zinc-500 dark:text-zinc-500 text-center max-w-sm">
                        {searchQuery || logFilter !== 'all' 
                          ? 'Try adjusting your search or filter criteria.' 
                          : 'Agent logs will appear here once the system starts processing.'}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="insights" className="space-y-6">
              <div className="flex flex-col items-center justify-center py-16 px-4">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-600 rounded-2xl blur-lg opacity-30"></div>
                  <div className="relative bg-gradient-to-r from-blue-600 to-blue-600 p-4 rounded-2xl">
                    <ThumbsUp className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-zinc-800 dark:text-zinc-200 mb-3 mt-6">Reddit Post Performance Coming Soon</h3>
                <p className="text-zinc-600 dark:text-zinc-400 text-center max-w-lg leading-relaxed mb-6">
                  We're working on tracking real Reddit performance metrics including <strong>karma (upvotes)</strong> and <strong>comment counts</strong> for all your published posts. This will show you which subreddits and content types perform best.
                </p>
                
                <div className="bg-zinc-50 dark:bg-zinc-800 rounded-lg p-6 max-w-md border border-zinc-200 dark:border-zinc-700">
                  <h4 className="font-semibold text-zinc-700 dark:text-zinc-300 mb-3 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    What's Coming:
                  </h4>
                  <ul className="space-y-2 text-sm text-zinc-600 dark:text-zinc-400">
                    <li className="flex items-center gap-2">
                      <ThumbsUp className="h-3 w-3 text-green-600 dark:text-green-400" />
                      Real karma/upvotes tracking
                    </li>
                    <li className="flex items-center gap-2">
                      <MessageCircle className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                      Comment count monitoring
                    </li>
                    <li className="flex items-center gap-2">
                      <TrendingUp className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                      Subreddit performance rankings
                    </li>
                    <li className="flex items-center gap-2">
                      <BarChart3 className="h-3 w-3 text-orange-600 dark:text-orange-400" />
                      Content type success rates
                    </li>
                  </ul>
                </div>

                <div className="flex items-center gap-3 mt-8">
                  <Button 
                    variant="outline" 
                    className="gap-2 border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800" 
                    onClick={() => setSelectedTab('agent-log')}
                  >
                    <Eye className="h-4 w-4" />
                    View Agent Activity
                  </Button>
                  <Button 
                    className="gap-2 bg-gradient-to-r from-blue-600 to-blue-600 hover:from-blue-700 hover:to-blue-700"
                    onClick={() => {
                      // Navigate to post scheduler
                      navigate(`/projects/${projectId}/reddit/content`);
                    }}
                  >
                    <Calendar className="h-4 w-4" />
                    Check Published Posts
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </div>
  )
}

export default function RedditAgentDashboard() {
  return (
    <RedditConnectionRequired
      feature="Reddit AI Agent"
      description="Autonomous Reddit posting agent that intelligently discovers communities, generates content, and schedules posts for optimal engagement. Let AI handle your Reddit strategy 24/7."
    >
      <RedditAgentDashboardContent />
    </RedditConnectionRequired>
  )
}